import React from "react";
import { ExternalLink, Check, GraduationCap, Sun, Package, Zap } from "lucide-react";
import { useQuote } from "./QuoteDialog";

const SUPPORT_POINTS = [
  { icon: GraduationCap, text: "Built specifically for K-12 schools" },
  { icon: Sun, text: "Indoor & outdoor printing capabilities" },
  { icon: Package, text: "Supplies, accessories & support in one place" },
  { icon: Zap, text: "Fast, professional-quality results" },
];

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_school-printing-hub/artifacts/wc2zq3i0_StockCake-Busy_school_hallway-1333185-medium.jpg";

export default function Hero() {
  const { openQuote } = useQuote();

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative isolate overflow-hidden bg-navy"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Bright school hallway with student displays and posters on walls"
          className="w-full h-full object-cover opacity-50"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F1F4D] via-[#0F1F4D]/85 to-[#0F1F4D]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F1F4D]/80 via-transparent to-transparent" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-24 pb-28 md:pt-32 md:pb-36 lg:pt-40 lg:pb-44">
        <div className="max-w-3xl">
          <div
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white text-xs font-bold uppercase tracking-[0.18em] mb-8 animate-fade-up"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-teal" />
            For K-12 Schools, Media Centers & Activity Departments
          </div>

          <h1
            data-testid="hero-headline"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tight text-white leading-[1.05] animate-fade-up"
            style={{ animationDelay: "80ms" }}
          >
            The Best Schools{" "}
            <span className="relative inline-block">
              <span className="relative z-10">Print In-House.</span>
              <span className="absolute left-0 right-0 bottom-1 h-3 bg-teal/70 -z-0 hidden sm:block" />
            </span>
            <br />
            <span className="text-teal">Does Yours?</span>
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-8 text-lg sm:text-xl text-slate-200 leading-relaxed max-w-2xl animate-fade-up"
            style={{ animationDelay: "160ms" }}
          >
            Give your staff the ability to create posters, banners, and school displays
            instantly—without waiting on outside vendors.
          </p>

          <div
            className="mt-10 animate-fade-up"
            style={{ animationDelay: "240ms" }}
          >
            <button
              data-testid="hero-request-quote-btn"
              onClick={openQuote}
              title="Opens in a new tab"
              className="group inline-flex items-center justify-center gap-2 bg-teal hover:bg-[#0D6BB1] text-white font-bold px-8 py-4 rounded-lg shadow-xl hover:shadow-2xl transition-all duration-200 hover:-translate-y-0.5"
            >
              Request a Quote
              <ExternalLink className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </div>

          <ul
            data-testid="hero-support-points"
            className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 max-w-2xl animate-fade-up"
            style={{ animationDelay: "320ms" }}
          >
            {SUPPORT_POINTS.map((p) => {
              const Icon = p.icon;
              return (
                <li
                  key={p.text}
                  className="flex items-start gap-3 text-slate-200"
                >
                  <span className="flex-shrink-0 mt-0.5 w-6 h-6 rounded-full bg-teal/20 border border-teal/40 flex items-center justify-center">
                    <Check className="w-3.5 h-3.5 text-teal" strokeWidth={3} />
                  </span>
                  <span className="text-sm font-medium">{p.text}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}
